import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Linking, Platform, Alert, ActivityIndicator } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';

const API_BASE = process.env.REACT_APP_SOCIAL_API_BASE
  || (process.env.EXPO_PUBLIC_SOCIAL_API_BASE)
  || (process.env.REACT_APP_VERCEL_URL ? `https://${process.env.REACT_APP_VERCEL_URL}` : null)
  || (process.env.EXPO_PUBLIC_VERCEL_URL ? `https://${process.env.EXPO_PUBLIC_VERCEL_URL}` : null)
  || 'https://www.chessmaps.app';
const DEBUG_OAUTH = process.env.REACT_APP_DEBUG_SOCIAL_OAUTH === 'true' || process.env.EXPO_PUBLIC_DEBUG_SOCIAL_OAUTH === 'true';

export default function SocialConnectionCard({
  clubId,
  connections = [],
  pendingDraftsCount = 0,
  loading = false,
  onConnectStart,
  onDisconnect,
  onReviewDrafts,
  onRefresh,
}) {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);

  const hasInstagram = connections.some((c) => c.platform === 'instagram');
  const hasFacebook = connections.some((c) => c.platform === 'facebook');
  const totalConnections = connections.length;
  const singlePlatform =
    totalConnections === 1 ? (connections[0].platform === 'instagram' ? 'Instagram' : 'Facebook') : null;

  const openOAuth = async (platform) => {
    try {
      onConnectStart?.();
      const url = `${API_BASE}/api/social/oauth-start?clubId=${encodeURIComponent(clubId)}&platform=${platform}`;
      const res = await fetch(url);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.message || `Server error (${res.status}). Social connect may not be configured — add META_APP_ID, SOCIAL_OAUTH_REDIRECT_URI, and SOCIAL_ENCRYPTION_KEY in Vercel.`;
        Alert.alert('Cannot start connection', msg);
        return;
      }
      if (data?.url) {
        if (DEBUG_OAUTH) {
          Alert.alert(
            'Debug: OAuth URL',
            'Copy this URL and open it in your browser to see the exact request Meta receives. Check the address bar and any error message.\n\n' + data.url,
            [{ text: 'OK' }]
          );
          if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(data.url);
          }
          return;
        }
        if (Platform.OS === 'web') {
          window.location.href = data.url;
        } else {
          const canOpen = await Linking.canOpenURL(data.url);
          if (canOpen) await Linking.openURL(data.url);
          else Alert.alert('Error', 'Cannot open browser');
        }
      } else {
        Alert.alert('Error', data?.message || 'Could not start connection');
      }
    } catch (e) {
      Alert.alert('Error', e.message || 'Failed to start connection');
    }
  };

  const handleDisconnect = (conn) => {
    const label = `${conn.platform} (${conn.platform_username || conn.platform_user_id})`;

    if (Platform.OS === 'web') {
      const ok = window.confirm(`Disconnect ${label}?`);
      if (ok) onDisconnect?.(conn.id);
      return;
    }

    Alert.alert(
      'Disconnect',
      `Disconnect ${label}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Disconnect', style: 'destructive', onPress: () => onDisconnect?.(conn.id) },
      ]
    );
  };

  return (
    <View style={[styles.card, { backgroundColor: colors.surface }]}>
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <View
            style={[
              styles.headerIconPill,
              { backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.25)' : 'rgba(148, 235, 152, 0.2)' },
            ]}
          >
            <Icon name="share" size={18} color="#1B5E20" />
          </View>
          <View>
            <Text style={[styles.title, { color: colors.text }]}>Social Sync</Text>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
              Turn your posts into announcements and event drafts.
            </Text>
          </View>
        </View>
        {pendingDraftsCount > 0 && (
          <TouchableOpacity
            style={[
              styles.headerDraftPill,
              {
                backgroundColor: isDarkMode ? colors.surface : '#E8F5E9',
                borderColor: colors.primary,
                borderWidth: 1,
              },
            ]}
            onPress={onReviewDrafts}
            activeOpacity={0.85}
          >
            <Icon name="article" size={14} color="#1B5E20" />
            <Text style={[styles.headerDraftPillText, { color: colors.primaryDark }]}>{pendingDraftsCount}</Text>
          </TouchableOpacity>
        )}
      </View>

      <>
        {loading && (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color="#666" />
            <Text style={[styles.loadingText, { color: colors.textSecondary }]}>Syncing recent posts…</Text>
          </View>
        )}

        <View
          style={[
            styles.connectionsCard,
            {
              backgroundColor: isDarkMode ? colors.backgroundDark : '#F8FAF8',
              borderColor: colors.borderLight,
            },
          ]}
        >
          <View style={styles.connectionsHeaderRow}>
            <Text style={[styles.connectionsTitle, { color: colors.primaryDark }]}>Connected accounts</Text>
            {totalConnections === 0 ? (
              <Text style={[styles.connectionsStatusText, { color: colors.textSecondary }]}>Not connected</Text>
            ) : (
              <Text style={[styles.connectionsStatusText, { color: colors.textSecondary }]}>
                {totalConnections === 1 ? '1 account' : `${totalConnections} accounts`}
              </Text>
            )}
          </View>

          {connections.map((conn) => (
            <View key={conn.id} style={styles.connectionRow}>
              <View style={styles.connectionLeft}>
                <View
                  style={[
                    styles.platformIconPill,
                    conn.platform === 'instagram' ? styles.platformInstagram : styles.platformFacebook,
                  ]}
                >
                  <Icon
                    name={conn.platform === 'instagram' ? 'camera-alt' : 'facebook'}
                    size={16}
                    color={conn.platform === 'instagram' ? '#fff' : '#1877F2'}
                  />
                </View>
                <View>
                  <Text style={[styles.connectionLabel, { color: colors.text }]}>
                    {conn.platform === 'instagram' ? 'Instagram' : 'Facebook'}
                    {conn.platform_username ? ` @${conn.platform_username}` : ''}
                  </Text>
                  <Text style={[styles.connectionSubLabel, { color: colors.textSecondary }]}>
                    {conn.is_active ? 'Active' : 'Paused'}
                  </Text>
                </View>
              </View>
              <TouchableOpacity onPress={() => handleDisconnect(conn)} style={styles.disconnectBtn}>
                <Icon name="link-off" size={16} color="#E53935" />
                <Text style={styles.disconnectText}>Disconnect</Text>
              </TouchableOpacity>
            </View>
          ))}

          {totalConnections === 0 && (
            <Text style={[styles.connectionsEmptyText, { color: colors.textSecondary }]}>
              Connect at least one Instagram or Facebook account to start pulling in posts.
            </Text>
          )}
        </View>

        {!hasInstagram && (
          <TouchableOpacity
            style={[
              styles.connectButton,
              { backgroundColor: isDarkMode ? colors.backgroundDark : '#f5f5f5' },
            ]}
            onPress={() => openOAuth('instagram')}
            activeOpacity={0.8}
          >
            <Icon name="camera-alt" size={20} color="#333" />
            <Text style={[styles.connectButtonText, { color: colors.text }]}>Connect Instagram</Text>
          </TouchableOpacity>
        )}
        {!hasFacebook && (
          <TouchableOpacity
            style={[
              styles.connectButton,
              { backgroundColor: isDarkMode ? colors.backgroundDark : '#f5f5f5' },
            ]}
            onPress={() => openOAuth('facebook')}
            activeOpacity={0.8}
          >
            <Icon name="facebook" size={20} color="#333" />
            <Text style={[styles.connectButtonText, { color: colors.text }]}>Connect Facebook</Text>
          </TouchableOpacity>
        )}

        {hasInstagram && (
          <View style={[styles.refreshRow, { borderTopColor: colors.border }]}>
            <Text style={[styles.refreshText, { color: colors.textSecondary }]}>
              Check for new Instagram posts.
            </Text>
            <TouchableOpacity
              style={[
                styles.refreshButton,
                { backgroundColor: isDarkMode ? colors.backgroundDark : '#f0f0f0' },
                loading && styles.refreshButtonDisabled,
              ]}
              onPress={onRefresh}
              disabled={loading}
              activeOpacity={0.8}
            >
              <Icon name="refresh" size={16} color="#333" />
              <Text style={[styles.refreshButtonText, { color: colors.text }]}>
                {loading ? 'Syncing…' : 'Fetch latest posts'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {pendingDraftsCount > 0 && (
          <TouchableOpacity
            style={[
              styles.draftsCta,
              { backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.2)' : '#E8F5E9' },
            ]}
            onPress={onReviewDrafts}
            activeOpacity={0.85}
          >
            <View style={[styles.draftsBadge, { backgroundColor: colors.primary }]}>
              <Text style={styles.draftsBadgeText}>{pendingDraftsCount}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.draftsCtaText, { color: colors.primaryDark }]}>
                {pendingDraftsCount} draft{pendingDraftsCount !== 1 ? 's' : ''}{' '}
                {singlePlatform ? `from ${singlePlatform}` : 'to review'}
              </Text>
              <Text style={[styles.draftsCtaSubtext, { color: colors.primary }]}>
                Review, edit, and publish to your club feed.
              </Text>
            </View>
            <Icon name="chevron-right" size={20} color="#1B5E20" />
          </TouchableOpacity>
        )}

        {hasInstagram && pendingDraftsCount === 0 && (
          <View style={styles.placeholderContainer}>
            <View style={styles.placeholderRow}>
              <View
                style={[
                  styles.placeholderCard,
                  { backgroundColor: isDarkMode ? colors.backgroundDark : '#f3f3f3', borderColor: colors.borderLight },
                ]}
              />
              <View
                style={[
                  styles.placeholderCard,
                  { backgroundColor: isDarkMode ? colors.backgroundDark : '#f3f3f3', borderColor: colors.borderLight },
                ]}
              />
              <View
                style={[
                  styles.placeholderCard,
                  { backgroundColor: isDarkMode ? colors.backgroundDark : '#f3f3f3', borderColor: colors.borderLight },
                ]}
              />
            </View>
            <Text style={[styles.placeholderText, { color: colors.textLight }]}>
              Your Instagram posts will show up here once we’ve synced them.
            </Text>
          </View>
        )}
      </>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  headerIconPill: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(148, 235, 152, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
    color: '#333',
  },
  subtitle: {
    fontSize: 12,
    color: '#777',
    marginTop: 2,
  },
  loadingRow: {
    paddingVertical: 12,
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  loadingText: {
    fontSize: 13,
    color: '#666',
  },
  connectionsCard: {
    marginTop: 8,
    marginBottom: 8,
    padding: 10,
    borderRadius: 10,
    backgroundColor: '#F8FAF8',
    borderWidth: 1,
    borderColor: '#E0EDE2',
  },
  connectionsHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  connectionsTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#2E7D32',
  },
  connectionsStatusText: {
    fontSize: 11,
    color: '#777',
  },
  connectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  connectionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  platformIconPill: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  platformInstagram: {
    backgroundColor: '#E1306C',
  },
  platformFacebook: {
    backgroundColor: 'rgba(24,119,242,0.08)',
    borderWidth: 1,
    borderColor: '#1877F2',
  },
  connectionLabel: {
    flex: 1,
    fontSize: 14,
    color: '#333',
  },
  connectionSubLabel: {
    fontSize: 11,
    color: '#888',
  },
  disconnectBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  disconnectText: {
    fontSize: 12,
    color: '#E53935',
  },
  connectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    marginTop: 4,
  },
  connectButtonText: {
    fontSize: 14,
    color: '#333',
    marginLeft: 8,
  },
  refreshRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  refreshText: {
    flex: 1,
    fontSize: 13,
    color: '#666',
    marginRight: 12,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
  },
  refreshButtonDisabled: {
    opacity: 0.6,
  },
  refreshButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    marginLeft: 4,
  },
  draftsCta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
  },
  draftsBadge: {
    backgroundColor: '#4CAF50',
    borderRadius: 12,
    minWidth: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  draftsBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  draftsCtaText: {
    flex: 1,
    fontSize: 13,
    color: '#2E7D32',
    fontWeight: '500',
  },
  draftsCtaSubtext: {
    fontSize: 11,
    color: '#4CAF50',
    marginTop: 2,
  },
  placeholderContainer: {
    marginTop: 14,
  },
  placeholderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  placeholderCard: {
    flex: 1,
    aspectRatio: 1,
    borderRadius: 10,
    backgroundColor: '#f3f3f3',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  placeholderText: {
    marginTop: 8,
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
  },
});
